import React, { memo, useMemo } from 'react';

import { createUseStyles, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getOverrides } from '@Sage/ssm-utils';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import styles from './styles';
const useStyles = createUseStyles(styles, 'EmptyScreen');

const EmptyScreen = ({
    className,
    buttons = [],
    title,
    description,
    illustration,
    iconType,
    overrides: overridesProp,
    onTracking,
    children,
}) => {
    const theme = useTheme();
    const override = getOverrides(overridesProp, EmptyScreen.overrides);
    const classes = useStyles();
    const rootClassNames = classnames(classes.root, className);
    const buttonList = buttons.map((button, index) => {
        if (!button) return null;

        return (
            <div className={classes.button} key={index}>
                {button}
            </div>
        );
    });

    const renderImageFirst = useMemo(
        () => (
            <div className={classes.emptyView} {...override.emptyView}>
                {illustration && (
                    <div className={classes.illustration} {...override.illustration}>
                        {illustration}
                    </div>
                )}
                <Text type="subtitle1" className={classes.title} {...override.title}>
                    {title}
                </Text>
                {description && (
                    <Text
                        type="caption"
                        color={theme.colors.textLight.secondary}
                        className={classes.description}
                        {...override.description}
                    >
                        {description}
                    </Text>
                )}
                {buttonList}
            </div>
        ),
        [
            buttonList,
            classes.description,
            classes.emptyView,
            classes.illustration,
            classes.title,
            description,
            illustration,
            override.description,
            override.emptyView,
            override.illustration,
            override.title,
            title,
            theme,
        ],
    );

    const renderImageLast = useMemo(
        () => (
            <div className={classes.emptyViewLast} {...override.emptyViewLast}>
                <Text type="subtitle1" className={classes.title} {...override.title}>
                    {title}
                </Text>
                <Text
                    type="caption"
                    color={theme.colors.textLight.secondary}
                    className={classes.description}
                    {...override.description}
                >
                    {description}
                </Text>
                <div
                    className={classes.illustration}
                    {...override.illustration}
                    onClick={onTracking}
                >
                    {illustration}
                </div>
                {buttonList}
            </div>
        ),
        [
            buttonList,
            classes.description,
            classes.emptyViewLast,
            classes.illustration,
            classes.title,
            description,
            illustration,
            onTracking,
            override.description,
            override.emptyViewLast,
            override.illustration,
            override.title,
            title,
            theme,
        ],
    );

    const renderContent = useMemo(() => {
        if (iconType === 'video') return renderImageLast;
        return renderImageFirst;
    }, [iconType, renderImageFirst, renderImageLast]);

    return (
        <div className={rootClassNames} {...override.root}>
            {renderContent}
            {children}
        </div>
    );
};

EmptyScreen.overrides = ['root', 'emptyView', 'illustration', 'title', 'description'];

EmptyScreen.propTypes = {
    description: PropTypes.string,
    buttons: PropTypes.arrayOf(PropTypes.element),
    illustration: PropTypes.element,
    title: PropTypes.string,
};

export default memo(EmptyScreen);
